import { range } from 'lodash';
import { useCallback, useState } from 'react';

const NEW_DASHBOARD_PREFIX = 'NewDashboard-';

function StorageUtilities(storage) {
    /**
     * Gets a dictionary of all storage keys/values.
     */
    function getDictionary() {
        return range(storage.length).reduce((dictionary, index) => {
            const key = storage.key(index);
            return {
                ...dictionary,
                [key]: storage.getItem(key),
            };
        }, {});
    }

    /**
     * Get a value by its key
     * @param key
     */
    function getValue(key) {
        // Return undefined for keys that start with `NewDashboard-`.  This is a precautionary
        // check so we don't return state that was previously saved during dashboard creation.
        if (key.startsWith(NEW_DASHBOARD_PREFIX)) return undefined;

        return storage.getItem(key);
    }

    /**
     * Local storage only stores strings, retrieve and manipulate
     * @param key
     */
    function getJSONValue(key, defaultValue) {
        const savedValueAsJSON = getValue(key);
        let returnValue = defaultValue;

        // If we have it parse it as JSON.
        if (savedValueAsJSON) {
            try {
                returnValue = JSON.parse(savedValueAsJSON);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(`Error parsing value ${savedValueAsJSON}`, error);
            }
        }

        return returnValue;
    }

    /**
     * Set a value by its key
     * @param key
     * @param value
     */
    function setValue(key, value) {
        // Ignore undefined values and keys that start with `NewDashboard-`.  The later
        // is necessary to prevent intermediate state from being saved during dashboard
        // creation, when the dashboard ID is not yet defined.
        if (!value || key.startsWith(NEW_DASHBOARD_PREFIX)) return;
        storage.setItem(key, value);
    }

    /**
     * Local storage only stores strings, manipulate and then store
     * @param key
     * @param value
     */
    function setJSONValue(key, value) {
        if (value === undefined) return;
        setValue(key, JSON.stringify(value));
    }

    /**
     * Remove a value
     * @param key
     */
    function removeValue(key) {
        storage.removeItem(key);
    }

    return {
        getDictionary,
        getValue,
        setValue,
        getJSONValue,
        setJSONValue,
        removeValue,
    };
}

export const LocalStorageUtilities = StorageUtilities(localStorage);
export const SessionStorageUtilities = StorageUtilities(sessionStorage);

/**
 * @template T
 * @typedef {[
 *      T,
 *      (value: T) => void,
 *      () => void
 * ]} StorageUtilityHookValue
 */

/**
 * @template T
 * @typedef {(key: string, initialValue? :T) => StorageUtilityHookValue<T>} StorageUtilityHook
 */

/** @type {StorageUtilityHook<string>} */
export const useLocalStorage = makeStorageUtilities(LocalStorageUtilities, false);

/** @type {StorageUtilityHook<string>} */
export const useSessionStorage = makeStorageUtilities(SessionStorageUtilities, false);

/** @type {StorageUtilityHook<any>} */
export const useLocalStorageJson = makeStorageUtilities(LocalStorageUtilities, true);

/** @type {StorageUtilityHook<any>} */
export const useSessionStorageJson = makeStorageUtilities(SessionStorageUtilities, true);

function makeStorageUtilities(storageUtility, useJson = true) {
    return function useStorageUtility(key, initialValue) {
        const [stateValue, setStateValue] = useState(getStorageValue(key, initialValue));

        const setValue = useCallback(
            (value) => {
                setStorageValue(key, value);
                setStateValue(value);
            },
            [key]
        );

        const removeValue = useCallback(() => {
            removeStorageValue(key);
            setStateValue(null);
        }, [key]);

        return [stateValue, setValue, removeValue];
    };

    function getStorageValue(key, defaultValue) {
        return storageUtility[useJson ? 'getJSONValue' : 'getValue'](key, defaultValue);
    }

    function setStorageValue(key, value) {
        return storageUtility[useJson ? 'setJSONValue' : 'setValue'](key, value);
    }

    function removeStorageValue(key) {
        return storageUtility.removeValue(key);
    }
}
